import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdminTrafficDetail } from '../../../services/adminApi';
import { Kpi } from './statsParts';
import { Tag } from '../catalog/ui';

// Trafic > Détail par jour (2026-11-03, demande du fondateur, consulté chaque jour) : chaque visite de la journée (heure de Paris)
// avec sa ville, sa provenance, robot ou personne, visiteur ou compte (jamais lequel), et les pages vues avec le temps passé.
// Par défaut les robots sont mis de côté (un filtre les montre) : ils ne comptent dans aucun total.

const FUSEAU = 'Europe/Paris';
export const FILTRES = ['personnes', 'humain', 'robot', 'tout'];

const garder = (filtre) => (v) => {
  if (filtre === 'tout') return true;
  if (filtre === 'personnes') return v.verdict !== 'robot';
  return v.verdict === filtre;
};

/** « 45 s », « 3 min 05 », « 1 h 12 » ; null -> null (inconnu, jamais inventé). */
export function duree(secondes) {
  if (secondes === null || secondes === undefined) return null;
  const s = Math.max(0, Math.round(secondes));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')}`;
  return `${Math.floor(s / 3600)} h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
}

const decalerJour = (jour, n) => {
  const d = new Date(`${jour}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const TONS = { humain: 'ok', incertain: 'warn', robot: 'mute' };

export default function TrafficDetail({ jour, filtre, onChange }) {
  const { t, i18n } = useTranslation('admin');
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const derniere = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniere.current;
    try {
      const r = await getAdminTrafficDetail({ jour });
      if (numero === derniere.current) { setData(r); setErreur(''); }
    } catch (err) {
      if (numero === derniere.current) setErreur(err?.message || t('stats.loadFailed'));
    }
  }, [jour, t]);
  useEffect(() => { setData(null); charger(); }, [charger]);

  let noms = null;
  try { noms = new Intl.DisplayNames([i18n.language], { type: 'region' }); } catch (_e) { noms = null; }
  const nomDuPays = (code) => {
    if (!code) return null;
    try { return (noms && noms.of(code)) || code; } catch (_e) { return code; }
  };
  const heure = (iso) => new Date(iso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', timeZone: FUSEAU });
  const jourLong = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

  const lieu = (v) => [v.city, nomDuPays(v.country)].filter(Boolean).join(', ') || t('stats.unknown');
  const provenance = (v) => {
    if (v.source === 'recherche' && v.engine) return t('stats.sources.searchEngine', { name: v.engine.charAt(0).toUpperCase() + v.engine.slice(1) });
    const nom = t(`stats.sources.${v.source}`, { defaultValue: v.source });
    return v.referrer && v.source !== 'direct' ? `${nom} · ${v.referrer}` : nom;
  };

  const leJour = data?.day || jour;
  const aujourdhui = data?.today;
  const visites = data ? data.visits.filter(garder(filtre)) : [];

  return (
    <>
      <div className="adm-actions adm-day-nav">
        <button type="button" className="btn btn-outline btn-sm" onClick={() => leJour && onChange({ jour: decalerJour(leJour, -1) })} disabled={!leJour}>
          {t('stats.detail.previous')}
        </button>
        <input
          type="date" className="adm-day-pick" aria-label={t('stats.detail.pickDay')}
          value={leJour || ''} max={aujourdhui || undefined} onChange={(e) => e.target.value && onChange({ jour: e.target.value })}
        />
        <button type="button" className="btn btn-outline btn-sm" onClick={() => onChange({ jour: decalerJour(leJour, 1) })} disabled={!leJour || !aujourdhui || leJour >= aujourdhui}>
          {t('stats.detail.next')}
        </button>
        {aujourdhui && leJour !== aujourdhui && (
          <button type="button" className="btn btn-outline btn-sm" onClick={() => onChange({ jour: '' })}>{t('stats.detail.today')}</button>
        )}
        <button type="button" className="btn btn-outline btn-sm" onClick={charger}>{t('stats.detail.refresh')}</button>
      </div>

      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      {!data && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}

      {data && (
        <>
          <h2 className="adm-day-title">{jourLong(data.day)}</h2>
          <div className="adm-kpis">
            <Kpi label={t('stats.detail.humans')} value={data.summary.humans} />
            <Kpi label={t('stats.detail.uncertain')} value={data.summary.uncertain}><small>{t('stats.detail.uncertainNote')}</small></Kpi>
            <Kpi label={t('stats.detail.robots')} value={data.summary.robots}><small>{t('stats.detail.robotsNote')}</small></Kpi>
            <Kpi label={t('stats.traffic.pageViews')} value={data.summary.pageViews}><small>{t('stats.detail.pageViewsNote')}</small></Kpi>
          </div>

          <div className="adm-chips" role="group" aria-label={t('stats.detail.filter')}>
            {FILTRES.map((f) => (
              <button key={f} type="button" className="adm-chip" aria-pressed={filtre === f} onClick={() => onChange({ voir: f })}>
                {t(`stats.detail.filters.${f}`)} <b>{data.visits.filter(garder(f)).length}</b>
              </button>
            ))}
          </div>

          {visites.length === 0 && <p className="adm-card adm-note">{t('stats.detail.empty')}</p>}

          <ul className="adm-visits">
            {visites.map((v) => {
              const total = duree(v.durationSeconds);
              return (
                <li key={`${v.start}-${v.pages[0]?.path}`}>
                  <details className="adm-visit">
                    <summary>
                      <span className="adm-visit-time">{heure(v.start)}</span>
                      <span className="adm-visit-main">
                        <span className="adm-visit-tags">
                          <Tag tone={TONS[v.verdict]}>{v.verdict === 'robot' ? t('stats.detail.robotNamed', { name: v.robot }) : t(`stats.detail.verdicts.${v.verdict}`)}</Tag>
                          <Tag tone={v.account === 'compte' ? '' : 'mute'}>{t(`stats.detail.accounts.${v.account || 'visiteur'}`)}</Tag>
                        </span>
                        <span className="adm-visit-where"><b>{lieu(v)}</b> · {provenance(v)} · {t(`stats.devices.${v.device}`, { defaultValue: t('stats.unknown') })}</span>
                      </span>
                      <span className="adm-visit-sum">
                        <b>{t('stats.detail.pages', { count: v.pages.length })}</b>
                        <small>{total || t('stats.detail.durationUnknown')}</small>
                      </span>
                    </summary>
                    <ol className="adm-visit-pages">
                      {v.pages.map((p, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={`${p.at}-${i}`}>
                          <span className="adm-visit-time">{heure(p.at)}</span>
                          <code>{p.path}</code>
                          <span>{duree(p.seconds) || <span className="adm-note" title={t('stats.detail.durationUnknownHelp')}>{t('stats.detail.durationUnknown')}</span>}</span>
                        </li>
                      ))}
                    </ol>
                  </details>
                </li>
              );
            })}
          </ul>

          <p className="adm-note">{t('stats.detail.rules')}</p>
          {data.truncated && <p className="adm-note">{t('stats.detail.truncated')}</p>}
        </>
      )}
    </>
  );
}
